// src/pages/VolpeTrackPage.js

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Page,
  Container,
  Breadcrumb,
  Header,
  HeaderLogo,
  Eyebrow,
  Title,
  Lead,
  Section,
  SectionTitle,
  Prose,
  CardGrid,
  Card,
  ButtonRow,
  StoreBadge,
  InlineLinks,
  Faq
} from '../components/ContentPage';
import { siteMeta, volpeTrackFacts, volpeTrackFaq } from '../seo/siteMeta';

const BRAND = '#694FA6';

const Intro = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: var(--spacing-xl);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  max-width: 280px;
  height: auto;
  justify-self: center;
  filter: drop-shadow(0 25px 50px rgba(0, 0, 0, 0.35));
`;

const LanguageList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    background: rgba(105, 79, 166, 0.25);
    border: 1px solid rgba(105, 79, 166, 0.5);
    color: var(--white);
    font-size: 0.9rem;
  }
`;

const VolpeTrackPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO routeKey="volpeTrack" />
      <Page>
        <Container>
          <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'VolpeTrack' }]} />

          <Intro>
            <div>
              <Header>
                <HeaderLogo
                  src="/logo-volpetrack.svg"
                  alt="VolpeTrack app icon"
                  width="96"
                  height="96"
                  style={{ background: BRAND }}
                />
                <div>
                  <Eyebrow $color="#B9A6F0">Available on iOS and Android</Eyebrow>
                  <Title>VolpeTrack: AI budget tracker</Title>
                </div>
              </Header>
              <Lead>
                VolpeTrack is an AI-powered personal finance assistant. It tracks income and
                expenses, categorizes transactions automatically, shows where your money goes, and
                answers questions about your finances through a built-in AI assistant. It is made by
                XADevs LLC and available in 10 languages.
              </Lead>
              <ButtonRow>
                <StoreBadge
                  href={siteMeta.productUrls.volpeTrackIOS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/assets/app-store.svg" alt="Download VolpeTrack on the App Store" width="162" height="48" />
                </StoreBadge>
                <StoreBadge
                  href={siteMeta.productUrls.volpeTrackAndroid}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/assets/google-play.svg" alt="Get VolpeTrack on Google Play" width="162" height="48" />
                </StoreBadge>
              </ButtonRow>
            </div>
            <PhoneImage
              src="/VolpeTrack-iPhone.webp"
              alt="VolpeTrack home screen on iPhone showing income, expenses, and the AI assistant"
              width="543"
              height="600"
              fetchPriority="high"
              decoding="async"
            />
          </Intro>

          <Section aria-labelledby="features-heading">
            <SectionTitle id="features-heading">What VolpeTrack does</SectionTitle>
            <CardGrid>
              {volpeTrackFacts.features.map((feature) => (
                <Card key={feature.title}>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </Card>
              ))}
            </CardGrid>
          </Section>

          <Section aria-labelledby="plans-heading">
            <SectionTitle id="plans-heading">Plans</SectionTitle>
            <CardGrid>
              {volpeTrackFacts.plans.map((plan) => (
                <Card key={plan.name}>
                  <h3>{plan.name}</h3>
                  <p>{plan.text}</p>
                </Card>
              ))}
            </CardGrid>
            <Prose>
              <p style={{ marginTop: 'var(--spacing-md)' }}>
                Prices are shown in the App Store and Google Play in your local currency.
              </p>
            </Prose>
          </Section>

          <Section aria-labelledby="languages-heading">
            <SectionTitle id="languages-heading">Available in 10 languages</SectionTitle>
            <LanguageList>
              {volpeTrackFacts.languages.map((language) => (
                <li key={language.code} lang={language.code}>{language.name}</li>
              ))}
            </LanguageList>
          </Section>

          <Section aria-labelledby="faq-heading">
            <SectionTitle id="faq-heading">Frequently asked questions</SectionTitle>
            <Faq items={volpeTrackFaq} />
          </Section>

          <Section aria-labelledby="legal-heading">
            <SectionTitle id="legal-heading">Support and legal</SectionTitle>
            <InlineLinks>
              <Link to="/support">Support Center</Link>
              <Link to="/volpetrack/privacy">VolpeTrack Privacy Policy</Link>
              <Link to="/volpetrack/terms">VolpeTrack Terms and Conditions</Link>
              <Link to="/volpetrack/account-deletion">Delete your account</Link>
            </InlineLinks>
          </Section>
        </Container>
      </Page>
    </>
  );
};

export default VolpeTrackPage;
